'use client';

import React, { useContext } from 'react';
import { Box } from '@mui/material';
import { ReactReduxContext } from 'react-redux';
import { Typography, Button, LayoutContainer } from '../kit';
import GenericErrorBoundary from './GenericErrorBoundary';

// Class component to catch errors
class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// Wrapper functional component to access Redux
function ErrorBoundary({ children }) {
  const reduxContext = useContext(ReactReduxContext);

  // Safely get auth state and user type from Redux
  let isAuthenticated = false;
  let isBusiness = false;
  if (reduxContext?.store) {
    const state = reduxContext.store.getState();
    isAuthenticated = state?.auth?.isLoggedIn;
    const userType = state?.auth?.userType || state?.userProfile?.data?.user?.userType;
    isBusiness = userType?.toLowerCase() === 'business';
  }

  // For non-authenticated users, use GenericErrorBoundary (no sidebar/layout)
  if (!isAuthenticated) {
    return <GenericErrorBoundary>{children}</GenericErrorBoundary>;
  }

  // For authenticated users, show error within LayoutContainer
  const handleReload = () => {
    window.location.href = isBusiness ? '/business-dashboard' : '/home';
  };

  const fallbackUI = (
    <LayoutContainer>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
      >
        <Typography variant="h4" color="error" gutterBottom>
          Something went wrong.
        </Typography>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          Please try going back or return to the dashboard.
        </Typography>
        <Box mt={2} display="flex" gap={2}>
          <Button variant="outlined" color="primary" onClick={() => window.history.back()}>
            Back
          </Button>
          <Button variant="contained" color="primary" onClick={handleReload}>
            Go to Dashboard
          </Button>
        </Box>
      </Box>
    </LayoutContainer>
  );

  return <ErrorBoundaryClass fallback={fallbackUI}>{children}</ErrorBoundaryClass>;
}

export default ErrorBoundary;
